import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { FlexColumn, FlexRow, Gap } from 'ui-components/components/flex';
import { Text, TextType } from 'ui-components/components/text';

import { IntlUnitFormattingService } from '../../../../../services/intl-unit-formatting-services/intl-unit-formatting-service';
import { convertAndSumWeightValues } from '../../../../../services/unit-services/weights/weight-convert-and-sum-service';
import Global from '../../../../../store/global-selectors';
import { FormattedCurrency } from '../../../../formatted-currency';
import { messages } from './summary-panel-messages';
import style from './summary-panel.module.css';

import type { Package } from '../../types';

type SummaryPanelProps = {
  packages: Package[];
};

export const SummaryPanel = ({ packages }: SummaryPanelProps) => {
  const sellerDefaultWeightUnit = useSelector(Global.selectSellerDefaultWeightUnit);
  const sellerDefaultCurrencyCode = useSelector(Global.selectSellerDefaultCurrency);

  const packagesCount = packages.length;

  const totalWeight = useMemo(
    () =>
      IntlUnitFormattingService.formatWeight(
        convertAndSumWeightValues(
          packages.map((p) => p.weight),
          sellerDefaultWeightUnit,
        ),
        sellerDefaultWeightUnit,
      ),
    [packages, sellerDefaultWeightUnit],
  );

  const totalInsurance = useMemo(
    () =>
      packages.reduce(
        (sum, p) => {
          const value = sum.value + (p.insuredValue?.value ?? 0);
          const code = sum.code || p.insuredValue?.code || sellerDefaultCurrencyCode;
          return { value, code };
        },
        { value: 0, code: sellerDefaultCurrencyCode },
      ),
    [packages, sellerDefaultCurrencyCode],
  );

  return (
    <FlexColumn gap={Gap.Space4} className={style['right-pane']}>
      <FlexRow gap={Gap.Space16} className={style['package-count']}>
        <FlexRow className={style.badge}>{packagesCount}</FlexRow>
        <Text textType={TextType.H3} value={messages.sidePanelPackagesLabel} formattedMessageValues={{ count: packagesCount }} />
      </FlexRow>

      <FlexRow className={style['details-row']} gap={Gap.Space8}>
        <Text textType={TextType.Paragraph} value={messages.sidePanelTotalWeightLabel} />
        <Text textType={TextType.Label} value={totalWeight || ''} />
      </FlexRow>

      <FlexRow className={style['details-row']} gap={Gap.Space8}>
        <Text textType={TextType.Paragraph} value={messages.sidePanelTotalInsuranceLabel} />
        <Text textType={TextType.Label} value={<FormattedCurrency value={totalInsurance.value} currencyCode={totalInsurance.code} />} />
      </FlexRow>
    </FlexColumn>
  );
};
